'use client';

import { aboutServiceTabs } from '@/constants';
import { useInView } from '@/lib/hooks';
import { cn, formatNumber, isVideo } from '@/lib/utils';
import { AboutServiceSectionTabEnum, PathEnum } from '@/types/enums';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import Button from '../ui/Button';
import H2 from '../ui/H2';
import Link from '../ui/Link';
import AnimatedNumberSection from '../ui/Numbers';
import AboutServiceInfo from './AboutServiceInfo';
import AboutServiceSectionTabButton from './AboutServiceSectionTabButton';
import ImagesCarousel from './ImagesCarousel';
import Video from './Video';

type Data = {
    mediaSrc: string | string[];
    service: {
        title: string;
        description: string;
    };
    process: {
        title: string;
        description: string;
    };
    specifications: {
        title: string;
        description: string;
    };
    utilizations: {
        title: string;
        description: string;
    };
};

export default function AboutServiceSection({
    serviceData,
    manufacturedParts,
}: {
    serviceData: Data;
    manufacturedParts: number;
}) {
    const t = useTranslations();

    const [activeTab, setActiveTab] = useState<string>(AboutServiceSectionTabEnum.Service);

    const handleTabClick = (id: string) => {
        if (activeTab !== id) {
            setActiveTab(id);
            window.history.pushState(null, '', `#${id}`);
        }
    };

    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        if (aboutServiceTabs(t).some((tab) => tab.id === hash)) {
            setActiveTab(hash);
            document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [t]);

    return (
        <section className="bg-white w-full pt-20 lg:pt-[60px]">
            <div className="flex justify-between max-lg:flex-col lg:container lg:px-0 mx-4 lg:mx-auto border-b border-light-grey">
                {aboutServiceTabs(t).map(({ id, label }) => (
                    <AboutServiceSectionTabButton
                        id={id}
                        label={label}
                        activeTab={activeTab}
                        onTabClick={() => handleTabClick(id)}
                        key={id}
                    />
                ))}
            </div>

            <div className="tab-content container mx-auto px-0 max-lg:px-4 lg:pb-[70px] pb-20">
                {activeTab === AboutServiceSectionTabEnum.Service && (
                    <AboutServiceInfoBlock
                        title={serviceData.service.title}
                        description={serviceData.service.description}
                        mediaSrc={serviceData.mediaSrc}
                        manufacturedParts={manufacturedParts}
                    />
                )}

                {activeTab === AboutServiceSectionTabEnum.Process && (
                    <AboutServiceInfoBlock
                        title={serviceData.process.title}
                        description={serviceData.process.description}
                        mediaSrc={serviceData.mediaSrc}
                        manufacturedParts={manufacturedParts}
                    />
                )}

                {activeTab === AboutServiceSectionTabEnum.Specifications && (
                    <AboutServiceInfoBlock
                        title={serviceData.specifications.title}
                        description={serviceData.specifications.description}
                        mediaSrc={serviceData.mediaSrc}
                        manufacturedParts={manufacturedParts}
                    />
                )}

                {activeTab === AboutServiceSectionTabEnum.Utilizations && (
                    <AboutServiceInfoBlock
                        title={serviceData.utilizations.title}
                        description={serviceData.utilizations.description}
                        mediaSrc={serviceData.mediaSrc}
                        manufacturedParts={manufacturedParts}
                    />
                )}

                <AboutServiceInfo />
            </div>
        </section>
    );
}

const CommonGetOfferSection = ({ manufacturedParts }: { manufacturedParts: number }) => {
    const t = useTranslations();
    const [hash, setHash] = useState<string | null>(null);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHash(window.location.hash);
        }
    }, []);

    return (
        <>
            <Link
                className="get-offer-trigger w-fit block mb-10 lg:mb-[60px]"
                href={PathEnum.Contact}
            >
                <Button
                    className="mt-7"
                    title={t('Buttons.getOffer')}
                    withArrow
                    variant="secondary"
                />
            </Link>
            {!hash ? (
                <AnimatedNumberSection
                    number={manufacturedParts}
                    className="text-[130px] max-lg:text-[80px] leading-tight opacity-35"
                    maxWidth={300}
                    dark
                />
            ) : (
                <div className="text-[130px] max-lg:text-[80px] leading-tight opacity-35 relative w-fit">
                    <span className="absolute top-0 right-0 text-sm text-dark-grey">
                        {t('HomePage.manufacturedParts')}
                    </span>
                    {formatNumber(manufacturedParts)}
                </div>
            )}
        </>
    );
};

const AboutServiceInfoBlock = ({
    title,
    description,
    mediaSrc,
    manufacturedParts,
}: {
    title: string;
    description: string;
    mediaSrc: string | string[];
    manufacturedParts: number;
}) => {
    const { isInView, elementRef } = useInView();

    const mediaIsArray = Array.isArray(mediaSrc);

    const carouselImages = mediaIsArray ? mediaSrc : [mediaSrc];

    return (
        <div
            className={cn(
                'flex justify-between lg:gap-[98px] max-lg:flex-col py-[60px] lg:py-[110px] opacity-0 transition-all duration-500',
                isInView && 'opacity-100',
            )}
            ref={elementRef}
        >
            <div className="lg:w-3/5 w-full flex flex-col leading-tight">
                <H2>{title}</H2>
                <h5 className="text-h5 mt-7 lg:mt-5">{description}</h5>
                <CommonGetOfferSection manufacturedParts={manufacturedParts} />
            </div>
            <div className="lg:w-2/5 w-full">
                <div className="relative h-[240px] lg:h-[420px] w-full overflow-hidden max-lg:mt-[60px] group">
                    {!mediaIsArray && isVideo(mediaSrc) ? (
                        <Video src={mediaSrc} posterSrc="/img/map_bg.webp" />
                    ) : (
                        <ImagesCarousel images={carouselImages} />
                    )}
                </div>
            </div>
        </div>
    );
};
